import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {toast} from '@common-web/hooks/use-toast';
import {PROTECTED_PERMISSION_KEYS, SCOPE_LABELS, SCOPE_OPTIONS} from '@common/permissions';
import {ArrowLeft, AlertTriangle, Save, UserCog, Trash2, History, Info, Users} from 'lucide-react';
import {RoleStatusBadge} from '@internal/components/roles-permissions/RoleStatusBadge';
import {PermissionPicker} from '@internal/components/roles-permissions/PermissionPicker';
import {RoleAuditLog} from '@internal/components/roles-permissions/RoleAuditLog';
import type {RoleScope} from '@internal/components/roles-permissions/types';

export function RoleDetail() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();

  const {data, isLoading, error, refetch} = trpc.internal.roles.get.useQuery(
    {id: id!},
    {enabled: !!id},
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<RoleScope>('CLIENT');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setDescription(data.description ?? '');
    setScope(data.scope);
    setSelectedPermissionIds(new Set(data.permissions.map((rp) => rp.permissionId)));
  }, [data]);

  const utils = trpc.useUtils();

  const updateMutation = trpc.internal.roles.update.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.internal.roles.list.invalidate(), refetch()]);
      toast({title: 'Role updated'});
    },
    onError: createMutationErrorHandler('Failed to update role'),
  });

  const refreshUser = useAuthStore((state) => state.refreshUser);

  const setPermissionsMutation = trpc.internal.roles.setPermissions.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.internal.roles.list.invalidate(),
        utils.internal.roles.audit.list.invalidate({roleId: data?.id ?? ''}),
        refetch(),
        // The editing admin may hold this role themselves — re-fetch the
        // session so their own permission gating reflects the new edges
        // immediately instead of on the next page load.
        refreshUser(),
      ]);
      toast({title: 'Permissions updated'});
    },
    onError: createMutationErrorHandler('Failed to update permissions'),
  });

  const deleteMutation = trpc.internal.roles.delete.useMutation({
    onSuccess: async () => {
      await utils.internal.roles.list.invalidate();
      toast({title: 'Role deleted'});
      navigate('/permissions?tab=roles');
    },
    onError: createMutationErrorHandler('Failed to delete role'),
  });

  const removeDeprecatedMutation = trpc.internal.roles.removeDeprecated.useMutation({
    onSuccess: async () => {
      await utils.internal.roles.list.invalidate();
      toast({title: 'Role removed'});
      navigate('/permissions?tab=roles');
    },
    onError: createMutationErrorHandler('Failed to remove role'),
  });

  if (isLoading) {
    return (
      <div className='max-w-4xl mx-auto p-8 space-y-4'>
        <Skeleton className='h-8 w-1/2' />
        <Skeleton className='h-32 w-full' />
        <Skeleton className='h-64 w-full' />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className='max-w-4xl mx-auto p-8'>
        <Alert variant='destructive'>
          <AlertTriangle className='h-4 w-4' />
          <AlertTitle>Role not found</AlertTitle>
          <AlertDescription>
            {error?.message ?? 'This role may have been removed.'}
          </AlertDescription>
        </Alert>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => navigate('/permissions?tab=roles')}
        >
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to roles
        </Button>
      </div>
    );
  }

  const isSystem = data.source === 'SYSTEM';
  const isDeprecated = data.isDeprecated;

  // Protected keys the role already holds (e.g. `tenant.access_all`) render
  // as locked in the picker — removing them would break most of the portal
  // for this role's users, and the server refuses such removals anyway.
  const lockedPermissionIds = new Set(
    data.permissions
      .filter((rp) => PROTECTED_PERMISSION_KEYS.has(rp.permission.key))
      .map((rp) => rp.permissionId),
  );

  const handleSaveMetadata = () => {
    updateMutation.mutate({
      id: data.id,
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
      scope,
    });
  };

  const handleSavePermissions = () => {
    setPermissionsMutation.mutate({
      id: data.id,
      permissionIds: Array.from(selectedPermissionIds),
    });
  };

  const handleDelete = () => {
    if (data.profileCount > 0) {
      toast({
        title: 'Cannot delete',
        description: `${data.profileCount} user(s) still have this role assigned.`,
        variant: 'destructive',
      });
      return;
    }
    if (!confirm(`Delete "${data.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate({id: data.id});
  };

  const handleRemoveDeprecated = () => {
    if (data.profileCount > 0) {
      toast({
        title: 'Cannot remove',
        description: `${data.profileCount} user(s) still have this role assigned.`,
        variant: 'destructive',
      });
      return;
    }
    if (
      !confirm(
        `Permanently remove "${data.name}"? Users who still have this role will lose its permissions.`,
      )
    ) {
      return;
    }
    removeDeprecatedMutation.mutate({id: data.id});
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='max-w-4xl mx-auto p-8 space-y-4'>
        <Button variant='ghost' onClick={() => navigate('/permissions?tab=roles')}>
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to roles
        </Button>

        {/* Summary card */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <div className='flex items-start justify-between'>
              <div className='space-y-1'>
                <CardTitle className='text-2xl font-bold text-brand-navy flex items-center gap-2'>
                  <UserCog className='h-5 w-5' />
                  {data.name}
                </CardTitle>
                <div className='flex items-center gap-2'>
                  <Badge variant='outline' className='text-xs'>
                    {SCOPE_LABELS[data.scope]?.split(' (')[0]}
                  </Badge>
                  {data.client && (
                    <Badge variant='outline' className='text-xs' title='Owning client tenant'>
                      {data.client.name}
                    </Badge>
                  )}
                  <RoleStatusBadge role={data} />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className='pt-5'>
            {data.description ? (
              <p className='text-sm text-brand-navy leading-relaxed'>{data.description}</p>
            ) : (
              <p className='text-sm text-brand-gray italic'>
                No description yet. Add one below so other admins understand what this role is for.
              </p>
            )}
          </CardContent>
        </Card>

        {isDeprecated && (
          <Alert variant='destructive'>
            <AlertTriangle className='h-4 w-4' />
            <AlertTitle>No longer in use</AlertTitle>
            <AlertDescription>
              This role is no longer maintained by the application. Reassign any users who still
              have it, then remove it.
            </AlertDescription>
          </Alert>
        )}

        {isSystem && data.isCustomized && (
          <Alert>
            <Info className='h-4 w-4' />
            <AlertTitle>Customized built-in role</AlertTitle>
            <AlertDescription>
              This built-in role's permissions have been edited by an admin and no longer follow the
              application defaults. Future application updates to this role's default permission set
              will not be applied automatically — manage its permissions here.
            </AlertDescription>
          </Alert>
        )}

        {/* Settings card */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>Settings</CardTitle>
          </CardHeader>
          <CardContent className='space-y-5 pt-6'>
            <div className='grid gap-1.5'>
              <Label htmlFor='role-name'>Display name</Label>
              <Input
                id='role-name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={updateMutation.isPending}
              />
            </div>

            <div className='grid gap-1.5'>
              <Label htmlFor='role-description'>Description</Label>
              <Textarea
                id='role-description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={updateMutation.isPending}
                placeholder='Describe who this role is for and what it grants.'
              />
            </div>

            <div className='grid gap-1.5'>
              <Label htmlFor='role-scope'>Who is this role for?</Label>
              <Select
                value={scope}
                onValueChange={(value) => setScope(value as RoleScope)}
                disabled={updateMutation.isPending}
              >
                <SelectTrigger id='role-scope' className='w-64'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SCOPE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-brand-gray'>
                Determines which types of users can be assigned this role.
              </p>
            </div>

            <div className='flex items-center justify-between pt-4 border-t'>
              <div className='flex gap-2'>
                {!isSystem && (
                  <Button
                    variant='destructive'
                    size='sm'
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending || data.profileCount > 0}
                    title={
                      data.profileCount > 0
                        ? `Cannot delete: ${data.profileCount} user(s) still have this role`
                        : undefined
                    }
                  >
                    <Trash2 className='h-4 w-4 mr-2' />
                    Delete role
                  </Button>
                )}
                {isSystem && isDeprecated && (
                  <Button
                    variant='destructive'
                    size='sm'
                    onClick={handleRemoveDeprecated}
                    disabled={removeDeprecatedMutation.isPending || data.profileCount > 0}
                    title={
                      data.profileCount > 0
                        ? `Cannot remove: ${data.profileCount} user(s) still have this role`
                        : undefined
                    }
                  >
                    <Trash2 className='h-4 w-4 mr-2' />
                    Remove role
                  </Button>
                )}
              </div>
              <Button onClick={handleSaveMetadata} disabled={updateMutation.isPending}>
                <Save className='h-4 w-4 mr-2' />
                Save changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Permissions card */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <div className='flex items-center justify-between'>
              <CardTitle className='text-lg font-semibold text-brand-navy'>Permissions</CardTitle>
              <Badge className='bg-brand-blue/10 text-brand-blue border-0 font-medium px-3 py-1'>
                {selectedPermissionIds.size} selected
              </Badge>
            </div>
          </CardHeader>
          <CardContent className='space-y-4 pt-6'>
            <p className='text-sm text-brand-gray'>
              Choose what users with this role are allowed to do. Internal roles can include any
              permission; other role types cannot include internal-only permissions.
              {isSystem &&
                !data.isCustomized &&
                ' Editing a built-in role takes ownership of its permissions — application updates to its defaults will no longer apply automatically.'}
            </p>
            <PermissionPicker
              // Local (possibly unsaved) scope so an edited scope immediately
              // drives the picker's compatibility filtering; the server
              // re-validates against the saved scope on submit.
              roleScope={scope}
              selectedIds={selectedPermissionIds}
              onChange={setSelectedPermissionIds}
              disabled={setPermissionsMutation.isPending}
              lockedIds={lockedPermissionIds}
            />
            <div className='flex items-center justify-end gap-3 pt-4 border-t'>
              {scope !== data.scope && (
                <p className='text-sm text-brand-gray'>
                  Save the scope change above first — permissions are validated against the saved
                  scope.
                </p>
              )}
              <Button
                onClick={handleSavePermissions}
                // The server validates permissions against the SAVED scope, so
                // an unsaved scope edit would let the picker offer keys the
                // submit then rejects — block until the scope is saved.
                disabled={setPermissionsMutation.isPending || scope !== data.scope}
              >
                <Save className='h-4 w-4 mr-2' />
                Save permissions
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Activity card */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
              <History className='h-4 w-4' />
              Recent activity
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-6'>
            <RoleAuditLog roleId={data.id} />
          </CardContent>
        </Card>

        {/* Assignments card */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
              <Users className='h-4 w-4' />
              Users with this role
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-6'>
            <p className='text-sm text-brand-navy'>
              <span className='text-lg font-semibold'>{data.profileCount}</span>{' '}
              {data.profileCount === 1 ? 'user has' : 'users have'} this role assigned.
            </p>
          </CardContent>
        </Card>

        {/* Technical details footer */}
        <Card className='border-gray-200 shadow-sm'>
          <CardContent className='py-4'>
            <div className='flex items-center gap-2 text-xs text-brand-gray'>
              <Info className='h-3.5 w-3.5 shrink-0' />
              <span>
                Role key: <code className='font-mono bg-gray-100 px-1 rounded'>{data.key}</code>
                {isSystem && ' · Built-in role managed by the application'}
                {!isSystem && ' · Custom role created by an admin'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
